import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdBanner } from "@/components/AdBanner";
import { 
  LayoutDashboard, 
  Calculator, 
  Timer, 
  Wrench, 
  Settings, 
  ChevronLeft,
  Menu,
  X,
  LogOut,
  User,
  ShieldCheck,
  MessageSquare,
  HelpCircle
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

export const Layout = ({ children, title, subtitle, backTo = "/dashboard", guest = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const logoSrc = theme === "light" ? "/logos/Evntkit_Logo.png" : "/logos/Evntkit_Logo_White.png";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const suites = [
    { 
      name: "Planning", 
      icon: Calculator, 
      color: "text-cyan-400",
      items: [
        { name: "Room Capacity", path: "/planning/room-capacity" },
        { name: "Catering", path: "/planning/catering" },
        { name: "Staffing", path: "/planning/staffing" },
        { name: "AV Specs", path: "/planning/av-specs" },
        { name: "WiFi Predictor", path: "/planning/wifi" },
        { name: "Floor Plan", path: "/planning/floor-plan" },
        { name: "Dancefloor", path: "/planning/dancefloor-calculator" },
      ]
    },
    { 
      name: "Execution", 
      icon: Timer, 
      color: "text-yellow-400",
      items: [
        { name: "Sync Timer", path: "/execution/timer" },
        { name: "Decibel Meter", path: "/execution/decibel" },
        { name: "Dinner Service Tally", path: "/execution/tally" },
      ]
    },
    { 
      name: "Solver", 
      icon: Wrench, 
      color: "text-purple-400",
      items: [
        { name: "Contract Scanner", path: "/solver/contract" },
        { name: "Emergency Script", path: "/solver/emergency" },
        { name: "Converter", path: "/solver/converter" },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Left: Back + Logo */}
            <div className="flex items-center gap-4">
              {!guest && location.pathname !== "/dashboard" && (
                <Link to={backTo}>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="back-button">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                </Link>
              )}
              <Link to={guest ? "/" : "/dashboard"} className="flex items-center gap-2" data-testid="logo-link">
                <img
                  src={logoSrc}
                  alt="evntkit"
                  className="h-10 sm:h-11 app-logo"
                />
              </Link>
            </div>

            {/* Center: Desktop Navigation */}
            {!guest && (
              <nav className="hidden md:flex items-center gap-6">
                {suites.map((suite) => (
                  <div key={suite.name} className="relative group">
                    <button className={`flex items-center gap-1.5 text-sm font-medium ${suite.color} hover:opacity-80 transition-opacity`}>
                      <suite.icon className="h-4 w-4" />
                      {suite.name}
                    </button>
                    <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div className="bg-card border border-border rounded-md p-2 min-w-[160px] shadow-lg">
                        {suite.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block px-3 py-2 text-sm rounded-sm hover:bg-accent transition-colors ${
                              location.pathname === item.path ? 'bg-accent' : ''
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </nav>
            )}

            {/* Right: User + Settings + Logout + Mobile Menu */}
            <div className="flex items-center gap-2">
              {guest ? (
                <span
                  className="suite-badge bg-secondary border-border text-foreground"
                  data-testid="guest-badge"
                >
                  Guest View
                </span>
              ) : (
                <>
                  {user && (
                    <div className="hidden sm:flex items-center gap-2 mr-2 pr-2 border-r border-border">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{user.name}</span>
                    </div>
                  )}
                  <Link to="/help">
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground" data-testid="help-button" title="Help Centre">
                      <HelpCircle className="h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/feedback">
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-cyan-400" data-testid="feedback-button" title="Send Feedback">
                      <MessageSquare className="h-5 w-5" />
                    </Button>
                  </Link>
                  {user?.is_admin && (
                    <Link to="/admin/console">
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-yellow-400" data-testid="admin-button" title="Admin Console">
                        <ShieldCheck className="h-5 w-5" />
                      </Button>
                    </Link>
                  )}
                  <Link to="/settings">
                    <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="settings-button">
                      <Settings className="h-5 w-5" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={handleLogout}
                    data-testid="logout-button"
                    title="Sign out"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 md:hidden"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    data-testid="mobile-menu-button"
                  >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {!guest && mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background">
            <div className="container mx-auto px-4 py-4 space-y-4">
              {suites.map((suite) => (
                <div key={suite.name}>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${suite.color} mb-2`}>
                    <suite.icon className="h-4 w-4" />
                    {suite.name}
                  </div>
                  <div className="grid grid-cols-2 gap-2 pl-6">
                    {suite.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              {user?.is_admin && (
                <div>
                  <Link
                    to="/admin/console"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-sm font-semibold text-yellow-400"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Admin Console
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Page Header */}
      {title && (
        <div className="border-b border-border/30 bg-card/30">
          <div className="container mx-auto px-4 py-6">
            <h1 className="text-3xl md:text-4xl tracking-tight uppercase">{title}</h1>
            {subtitle && (
              <p className="text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {children}
      </main>

      {/* Ad Banner */}
      <div className="container mx-auto px-4">
        <AdBanner />
      </div>

      {/* Footer */}
      <footer className="border-t border-border/30 mt-auto">
        <div className="container mx-auto px-4 py-4">
          <p className="text-xs text-muted-foreground text-center">
            evntkit • Planning & Delivery Tools for Event Professionals
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
